import { useCallback, useEffect, useRef, useState } from "react";

interface UseDashboardLiveRefreshOptions {
    /** When true, re-fetch on `pollIntervalMs`. */
    live: boolean;
    pollIntervalMs: number;
    /** Extra gate for polling (e.g. execution still running). Defaults to true. */
    shouldPoll?: boolean;
    /**
     * Load widget data. Two accepted return shapes:
     *  - `boolean` (legacy): `true` only when the fetch actually CHANGED the
     *    displayed data (or it was the first load); `false` when the poll
     *    succeeded but the data was byte-for-byte unchanged, or when it
     *    deferred / failed. A bare boolean is treated as a successful fetch
     *    (`ok: true`).
     *  - `{ changed, ok }` (rich): `changed` as above, plus an explicit `ok`
     *    flag — `ok: false` marks a fetch that did NOT successfully complete
     *    (an error, or a deferred no-op), so a manual refresh does not falsely
     *    stamp "Updated just now" over stale/errored data.
     * The return value gates `lastUpdatedAt`: an unchanged background `live`
     * re-poll neither stamps a new timestamp nor re-renders the widget, while a
     * user-triggered manual refresh that completes successfully always stamps
     * (see `refresh` below). Widgets back `changed` by diffing the new payload
     * against the last committed one and skipping their own setState when equal.
     */
    fetch: () => Promise<boolean | { changed: boolean; ok?: boolean }>;
}

/**
 * Shared polling / manual refresh for dashboard widgets with optional `live` config.
 * Tracks the last time the data actually CHANGED (not merely re-polled) for the
 * "Updated …" label — `fetch` returns `true` only on a real change, so an
 * unchanged `live` re-poll does not update the timestamp or re-render the widget.
 */
export const useDashboardLiveRefresh = ({
    live,
    pollIntervalMs,
    shouldPoll = true,
    fetch,
}: UseDashboardLiveRefreshOptions) => {
    const [lastUpdatedAt, setLastUpdatedAt] = useState<string | null>(null);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const isMounted = useRef(true);

    // `force` is set for a user-triggered manual refresh: stamp the "Updated …"
    // time on any SUCCESSFUL fetch (even one that found no change), so a manual
    // refresh of unchanged data still confirms "Updated just now" rather than
    // looking like the click did nothing. A background `live` re-poll never
    // forces, so an idle widget still neither re-stamps nor re-renders.
    const runFetch = useCallback(
        async (force = false) => {
            const result = await fetch();
            const changed =
                typeof result === "boolean" ? result : result.changed;
            const ok = typeof result === "boolean" ? true : result.ok !== false;
            if (isMounted.current && ok && (changed || force)) {
                setLastUpdatedAt(new Date().toISOString());
            }
            return changed;
        },
        [fetch],
    );

    const refresh = useCallback(async () => {
        setIsRefreshing(true);
        // Hold the spinner on screen for a minimum time. A dashboard re-fetch is
        // often sub-100ms, so without a floor the loading state toggles off the
        // same frame it turned on — the user sees the glyph blink out with no
        // perceptible spinner, reading as "the click did nothing". Running the
        // fetch and a short minimum-visible timer in parallel guarantees the
        // standard loading animation is actually seen.
        const minVisible = new Promise<void>((resolve) =>
            setTimeout(resolve, 500),
        );
        try {
            await Promise.all([runFetch(true), minVisible]);
        } finally {
            if (isMounted.current) {
                setIsRefreshing(false);
            }
        }
    }, [runFetch]);

    useEffect(() => {
        isMounted.current = true;
        runFetch();
        return () => {
            isMounted.current = false;
        };
    }, [runFetch]);

    useEffect(() => {
        if (!live || !shouldPoll) {
            return undefined;
        }

        let intervalId: ReturnType<typeof setInterval> | undefined;
        let startTimeoutId: ReturnType<typeof setTimeout> | undefined;

        // Poll only while the page is actually visible. A backgrounded tab (or a
        // minimised window) cannot show the data, so continuing to fetch + parse +
        // diff it just burns CPU and network in the background — pause entirely
        // when hidden and do one catch-up fetch when the page becomes visible again.
        const isHidden = () =>
            typeof document !== "undefined" && document.hidden;

        const stopPolling = () => {
            if (startTimeoutId !== undefined) {
                clearTimeout(startTimeoutId);
                startTimeoutId = undefined;
            }
            if (intervalId !== undefined) {
                clearInterval(intervalId);
                intervalId = undefined;
            }
        };

        const startPolling = () => {
            if (intervalId !== undefined || startTimeoutId !== undefined) {
                return;
            }
            // Jitter the first tick across [0, pollIntervalMs). Every widget on a
            // dashboard mounts in the same React commit, so without a phase offset
            // their intervals all fire on the same tick — a synchronized burst that
            // fetches, parses and diffs every widget's payload at once and pegs the
            // main thread. Desynchronising spreads that work across the interval.
            const firstDelay = Math.floor(Math.random() * pollIntervalMs);
            startTimeoutId = setTimeout(() => {
                startTimeoutId = undefined;
                if (!isHidden()) {
                    runFetch();
                }
                intervalId = setInterval(() => {
                    if (!isHidden()) {
                        runFetch();
                    }
                }, pollIntervalMs);
            }, firstDelay);
        };

        const onVisibilityChange = () => {
            if (isHidden()) {
                stopPolling();
            } else {
                // Became visible again: catch up immediately, then resume polling.
                runFetch();
                startPolling();
            }
        };

        if (!isHidden()) {
            startPolling();
        }
        if (typeof document !== "undefined") {
            document.addEventListener("visibilitychange", onVisibilityChange);
        }

        return () => {
            if (typeof document !== "undefined") {
                document.removeEventListener("visibilitychange", onVisibilityChange);
            }
            stopPolling();
        };
    }, [live, shouldPoll, pollIntervalMs, runFetch]);

    return { lastUpdatedAt, refresh, isRefreshing };
};
